<template>
    <div class="contacts-container">
        <van-field
            readonly
            clickable
            border
            :error="false"
            :placeholder="currentPlaceholder"
            :label="label"
            @click="openEditor"
            :right-icon="rightIcon"
            :closeable="false"
            class="contacts-field"
            :value="currentContacts"
            :rules="formRules.required"
        />
        <div class="contacts-result" v-if="contacts.length > 0">
            <div class="result" v-for="(item, index) in contacts" :key="index" :class="{ divider: index > 0 }">
                <div>
                    <div class="name">{{ item.name }}</div>
                    <div class="phone">{{ $t("registration.contactPhone") + ": " }}{{ item.phone }}</div>
                </div>
                <div class="operator" v-if="more">
                    <van-icon name="edit" size="16px" @click="selectItem(item, index)" />
                    <van-icon name="delete-o" size="16px" @click="deleteItem(index)" />
                </div>
            </div>
        </div>
        <van-popup v-model="showEditor" round position="bottom" :style="{ height: '70%' }">
            <div class="edit-contact-container">
                <div class="header">
                    <div @click="onCancel" class="cancel">{{ $t("common.btn.cancel") }}</div>
                    <div class="title">{{ label }}</div>
                    <div @click="onSave" :class="{ disabled: !this.name || !this.phone }" class="save">
                        {{ $t("common.btn.ok") }}
                    </div>
                </div>
                <div class="body">
                    <van-field
                        class="name"
                        :label="nameLabel"
                        :placeholder="$t('common.placeholder.input')"
                        v-model="name"
                    />
                    <van-field
                        class="phone"
                        :label="$t('registration.contactPhone')"
                        :placeholder="$t('common.placeholder.input')"
                        v-model="phone"
                    />
                </div>
            </div>
        </van-popup>
    </div>
</template>

<script>
import Vue from "vue";
import i18n from "@/locales/i18n";

export default Vue.extend({
    name: "contacts",
    data() {
        return {
            showEditor: false,
            name: "",
            phone: "",
            isEdit: false,
            index: -1,
            formRules: {
                required: [{ required: true, message: this.$t("common.error.required") }]
            },
            currentContacts: this.currentPlaceholder
        };
    },
    props: {
        placeholder: {
            type: String,
            default: i18n.t("common.placeholder.add")
        },
        rightIcon: {
            type: String,
            default: ""
        },
        label: {
            type: String,
            required: true
        },
        contacts: {
            type: Array,
            required: true
        },
        more: {
            type: Boolean,
            default: false
        },
        nameLabel: {
            type: String,
            default: i18n.t("common.base.name")
        },
        readOnly: {
            type: Boolean,
            default: false
        }
    },
    computed: {
        currentPlaceholder() {
            if (!this.more && (this.contacts?.length > 0 || (this.name && this.phone))) {
                return this.$t("edit");
            }
            if (this.contacts?.length > 0 || (this.name && this.phone)) {
                return this.$t("add");
            }
            return this.placeholder;
        }
    },
    mounted() {
        this.onCancel();
    },
    methods: {
        openEditor() {
            if (this.readOnly) {
                return;
            }
            if (!this.more && this.contacts?.length > 0) {
                this.selectItem(this.contacts[0], 0);
            } else {
                this.showEditor = true;
            }
        },
        onSave() {
            if (!this.name || !this.phone) {
                return;
            }
            this.currentContacts = this.currentPlaceholder;
            this.$emit("change", {
                name: this.name,
                phone: this.phone,
                isEdit: this.isEdit,
                index: this.index,
                more: this.more
            });
            this.showEditor = false;
            this.name = "";
            this.phone = "";
            this.isEdit = false;
            this.index = -1;
        },
        onCancel() {
            this.name = "";
            this.phone = "";
            this.showEditor = false;
            this.isEdit = false;
            this.index = -1;
            if (this.contacts?.length > 0) {
                this.currentContacts = this.currentPlaceholder;
            }
        },
        selectItem(item, inx) {
            if (this.readOnly) {
                return;
            }
            this.showEditor = true;
            this.name = item.name;
            this.phone = item.phone;
            this.isEdit = true;
            this.index = inx;
        },
        deleteItem(inx) {
            if (this.readOnly) {
                return;
            }
            if (this.contacts?.length > 2) {
                this.currentContacts = this.currentPlaceholder;
            } else {
                this.currentContacts = "";
            }
            this.$emit("delete", {
                index: this.index
            });
        }
    }
});
</script>

<style scoped lang="less">
.contacts-container {
    .contacts-field {
        &::after {
            border-bottom: unset;
        }
    }
    .contacts-result {
        display: flex;
        background: #fff;
        padding: 0 16px 16px;
        flex-direction: column;

        .divider {
            border-top: 1px solid #eceef2;
            margin-top: 16px;
            padding-top: 16px;
        }
        .result {
            display: flex;
            width: 100%;
            align-items: center;
            .operator {
                margin-left: auto;
                /deep/ .van-icon-edit {
                    color: var(--primary-brand-color);
                    margin-right: 18px;
                }
                /deep/ .van-icon-delete-o {
                    color: #f46e52;
                }
            }
        }
        .name {
            color: #3c3e46;
            font-size: 14px;
            font-style: normal;
            font-weight: 600;
            line-height: 22px;
            margin-bottom: 4px;
        }
        .phone {
            color: #6c717d;
            font-size: 13px;
            font-style: normal;
            font-weight: 400;
            line-height: 20px;
        }
    }
    .edit-contact-container {
        .header {
            display: flex;
            padding: 16px;
            align-items: center;
            flex-direction: row;
            flex: 1 0 0;
            justify-content: space-between;
            color: #232429;
            text-align: center;
            font-size: 16px;
            font-style: normal;
            font-weight: 600;
            line-height: 16px;
            .cancel {
                font-size: 14px;
            }
            .save {
                font-size: 14px;
                color: var(--primary-brand-color);

                &.disabled {
                    opacity: 0.5;
                }
            }
        }
    }
}
</style>
